import React from 'react';
import { Table, Tag, Button } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { MaturityScheduleResponse } from '../../model/MaturitySchedule.model';
import { useCardDetail } from '../../context/CardDetailContext';

interface CreatedTodayTableProps {
  data: MaturityScheduleResponse[];
  loading: boolean;
}

const CreatedTodayTable: React.FC<CreatedTodayTableProps> = ({
  data,
  loading,
}) => {
  const navigate = useNavigate();
  const { setSelectedCard, setMaturityScheduleId, setMaturityYear } =
    useCardDetail();

  const handleView = (record: MaturityScheduleResponse) => {
    if (record.creditCard) {
      // Set data in context
      setSelectedCard(record.creditCard);
      setMaturityScheduleId(record.id);
      setMaturityYear(new Date(record.dueDate).getFullYear());

      // Navigate to card detail page without params
      navigate('/card-detail');
    }
  };

  const columns = [
    {
      title: 'Hành động',
      key: 'actions',
      width: 100,
      fixed: 'right' as const,
      render: (_: any, record: MaturityScheduleResponse) => (
        <Button
          type="link"
          onClick={() => handleView(record)}
          className="text-blue-600 hover:text-blue-800"
        >
          Xem
        </Button>
      ),
    },
  ];

  const baseColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Ngày đáo hạn',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 120,
      render: (dueDate: string) => dayjs(dueDate).format('DD/MM/YYYY'),
    },
    {
      title: 'Số tiền',
      dataIndex: 'repaymentAmount',
      key: 'repaymentAmount',
      width: 120,
      render: (amount: number) => (
        <span className="font-semibold text-blue-600">
          {amount.toLocaleString()}
        </span>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        let color = 'default';
        let text = status;
        switch (status) {
          case 'PENDING':
            color = 'orange';
            text = 'Đang chờ';
            break;
          case 'OVERDUE':
            color = 'red';
            text = 'Quá hạn';
            break;
          case 'PAID':
            color = 'blue';
            text = 'Đã nạp đáo';
            break;
          case 'COMPLETED':
            color = 'green';
            text = 'Hoàn thành';
            break;
          case 'ABANDONED':
            color = 'default';
            text = 'Không làm';
            break;
        }
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: 'Phí thu (%)',
      dataIndex: 'feeApplied',
      key: 'feeApplied',
      width: 100,
      render: (fee: number) => fee?.toFixed(2),
    },
    {
      title: 'Số thẻ',
      key: 'cardNumber',
      width: 120,
      render: (_: any, record: MaturityScheduleResponse) =>
        record.creditCard?.cardNumber || 'N/A',
    },
    {
      title: 'Chủ thẻ',
      key: 'cardHolderName',
      width: 140,
      render: (_: any, record: MaturityScheduleResponse) =>
        record.creditCard?.cardHolderName || 'N/A',
    },
    {
      title: 'Nhân viên',
      key: 'employees',
      width: 180,
      render: (_: any, record: MaturityScheduleResponse) =>
        record.employees?.map((e) => e.name).join(', '),
    },
    {
      title: 'Ghi chú',
      dataIndex: 'notes',
      key: 'notes',
      width: 200,
      render: (notes: string) =>
        notes || <span className="text-gray-400">-</span>,
    },
  ];

  return (
    <Table
      columns={[...baseColumns, ...columns]}
      dataSource={data}
      loading={loading}
      rowKey="id"
      pagination={{ pageSize: 20 }}
      bordered
      scroll={{ x: 1300 }}
      locale={{ emptyText: 'Không có kỳ đáo hạn nào được tạo hôm nay.' }}
    />
  );
};

export default CreatedTodayTable;
